import { useEffect, useRef, useState } from "react";
import useUploadFile from "../../../../../../Components/FileUploader/fileUploader-hooks";
import { ImageTypes, MeasureTypes, TimeUnitTypes } from "../../../../../../Interfaces/componentTypes";
import { useDirectoriesHook, useGeneralHooks, useToastify } from "../../../../../../Hooks/hooks";
import { useFieldArray, useForm } from "react-hook-form";
import { ICreateMenuItemForm, IMenuItemFormOption } from "../../../../../../Interfaces/formValue-interfaces";
import { useUpdateMenuItemMutation } from "../../../../../../API/authApi";
import { IMenuItemOption, IUpdateMenuItem } from "../../../../../../Interfaces/request-interfaces";
import { IEditMenuItemProps } from "./editMenuItem";
import useLanguagesHooks from "../../../../../../Components/Languages/setLanguage-hooks";

const useEditMenuItem = (props: IEditMenuItemProps) => {
    const { data, onClose, cb } = props;
    const { t } = useLanguagesHooks();
    const { category_id, company_id, id, image } = data;
    const { imageUrl, handleFileInputChange, ImageResponce, imageLoading } = useUploadFile(ImageTypes.CATEGORY);
    const [updateItem, { isLoading: updateLoading, isSuccess: updateSuccess, isError: updateError }] = useUpdateMenuItemMutation();
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const { menuItemMeasureTypes, timeUnits } = useDirectoriesHook();
    const [showTranslations, setShowTranslations] = useState<boolean>(false);
    const { baseURL } = useGeneralHooks();
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormChanged, setIsFormChanged] = useState(false);
    const { showToastSuccess, showToastError } = useToastify();
    const formValidator = isFormValid && isFormChanged;
    const originalDataRef = useRef<ICreateMenuItemForm>({
        name_hy: data.name_hy,
        name_en: data.name_en,
        name_ru: data.name_ru,
        description_hy: data.description_hy,
        description_en: data.description_en,
        description_ru: data.description_ru,
        cooking_time: data.cooking_time,
        cooking_time_unit: timeUnits.find((unit) => unit.id === data.cooking_time_unit),
        option: data.option.map((option) => ({
            is_active: option.is_active,
            measure_type: menuItemMeasureTypes.find((type) => type.id === option.measure_type) ?? undefined,
            measure_value: option.measure_value,
            price: option.price,
            id: option.option_id,
        })),
    });

    const {
        register,
        control,
        formState: { errors },
        setValue,
        getValues,
        watch,
    } = useForm<ICreateMenuItemForm>({
        mode: 'all',
        defaultValues: {
            name_hy: data.name_hy,
            name_en: data.name_en,
            name_ru: data.name_ru,
            description_hy: data.description_hy,
            description_en: data.description_en,
            description_ru: data.description_ru,
            cooking_time: data.cooking_time,
            cooking_time_unit: timeUnits.find((unit) => unit.id === data.cooking_time_unit)
        }
    });
    const { fields, append, remove, update, } = useFieldArray({
        control,
        name: "option"
    });

    const validateForm = (): boolean => {
        const formValues = getValues();
        const { name_hy, option } = formValues;
        if (!name_hy || name_hy.trim() === "") return false;
        for (let i = 0; i < option.length; i++) {
            const { measure_type, price } = option[i];
            if (!measure_type || !price || price.trim() === "") return false;
        }

        return true;
    };


    const detectFormChanges = () => {
        const currentValues = getValues();
        const isChanged = JSON.stringify(currentValues) !== JSON.stringify(originalDataRef.current);
        setIsFormChanged(isChanged)
    };

    useEffect(() => {
        const subscription = watch(() => {
            detectFormChanges();
            const isValid = validateForm();
            setIsFormValid(isValid);
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    useEffect(() => {
        if (data) {
            remove();
            data.option.forEach((option) => {
                const optionField: IMenuItemFormOption = {
                    is_active: option.is_active,
                    measure_type: menuItemMeasureTypes.find((type) => type.id === option.measure_type) ?? undefined,
                    measure_value: option.measure_value,
                    price: option.price,
                    package_cost:option.package_cost,
                    option_id: option.option_id,
                };
                append(optionField)
            })
        }
    }, [data]);

    const addOption = () => {
        append({ measure_type: undefined, measure_value: "", price: undefined, is_active: false })
    };

    const deleteOption = (index: number) => {
        remove(index)
    };

    const handleShowTranslations = () => {
        setShowTranslations(prev => !prev)
    };

    useEffect(() => {
        if (updateSuccess) {
            showToastSuccess(t('Toast.Success.Common'));
            cb();
            onClose();

        } else if (updateError) {
            showToastError(t('Toast.Error.Common'))
        }
    }, [updateSuccess, updateError])

    const onUpdate = () => {
        const options: IMenuItemOption[] = fields.map((option) => {
            return {
                option_id: option.option_id,
                measure_type: option.measure_type?.id as MeasureTypes,
                measure_value: Number(option.measure_value) || 1,
                price: option.price!,
                package_cost:option.package_cost,
                is_active: option.is_active
            };
        })
        const payload: IUpdateMenuItem = {
            category_id: category_id,
            company_id: company_id,
            menu_item_id: id,
            image: ImageResponce ? ImageResponce.src : image,
            name_en: getValues().name_en,
            name_ru: getValues().name_ru,
            name_hy: getValues().name_hy,
            description_en: getValues().description_en,
            description_ru: getValues().description_ru,
            description_hy: getValues().description_hy,
            option: options,
            cooking_time: getValues('cooking_time')!,
            cooking_time_unit: getValues('cooking_time_unit')?.id as TimeUnitTypes,
        };
        updateItem(payload)
    };

    return {
        fields,
        errors,
        baseURL,
        control,
        imageUrl,
        dialogRef,
        timeUnits,
        imageLoading,
        updateLoading,
        formValidator,
        showTranslations,
        update,
        register,
        onUpdate,
        setValue,
        getValues,
        addOption,
        deleteOption,
        handleFileInputChange,
        handleShowTranslations,
    }
};

export default useEditMenuItem